import { memo } from "react";
import styled, { css } from "styled-components";
import { generic } from "../../../../../ui/_themes/constants";
import * as h from "../../../../../ui/_themes/helpers";
import {
  AutoplayButton,
  IAutoplayButton
} from "../../../../../ui/AutoplayButton";
import { CombinedSpinButton } from "../../../../../ui/CombinedSpinButton";
import { StyledAutoplayButton } from "../../../../../ui/CombinedSpinButton/components/AutoplayButton";
import { KeypadButton } from "../../../../../ui/KeypadButton";

const keypadButtonDesktopStyle = css`
  transition: color 50ms ease;
  display: block;
  width: 2.1em;
  height: 2.1em;
`;

export const StyledKeypadButton = memo(styled(KeypadButton)`
  ${keypadButtonDesktopStyle};
  align-self: center;
  &:hover {
    color: ${h.getHoverColor("button")};
  }

  &:active {
    color: ${h.getPressedColor("button")};
  }

  &[disabled] {
    color: ${h.getDisabledColor("button")};
  }
`);

export const SpinControlKeypadButton = styled(KeypadButton)`
  overflow: visible;
  align-self: center;
  color: ${h.getColor("spinControlButton")};

  &:hover {
    color: ${h.getHoverColor("spinControlButton")};
  }

  &:active {
    color: ${h.getPressedColor("spinControlButton")};
  }

  &[disabled] {
    color: ${h.getDisabledColor("spinControlButton")};
  }

  > svg {
    ${keypadButtonDesktopStyle};
    --quickSpinActiveIconColor: ${h.getActiveColor("spinControlButton")};
  }
`;

export const StyledCombinedSpinButton = memo(styled(CombinedSpinButton)`
  ${({ autoplayActive, autoplayEnabled, autoplayRunning }) => {
    const isAutoplayButton =
      (autoplayActive && autoplayRunning) || autoplayEnabled;

    if (isAutoplayButton) {
      return `
        &:hover {
          background: ${h.getHoverColor(
            "autoplayButtonBackground",
            "autoplayButton.background"
          )};
          color: ${h.getHoverColor(
            "autoplayButtonIcon",
            "autoplayButton.icon"
          )};
        }
      `;
    }
    return `
      &:hover {
        background: ${h.getHoverColor(
          "spinButtonBackground",
          "spinButton.background"
        )};
        color: ${h.getHoverColor(
          "spinButtonForeground",
          "spinButton.foreground"
        )};
      }
    `;
  }};

  width: 4.1em;
  height: 4.1em;
  z-index: ${generic.layers.settings + 1};

  > svg {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }

  /* Autoplay counter */
  &${StyledAutoplayButton}::before {
    z-index: ${generic.layers.game + 2};
  }
`);

/* Minimal buttons */
const MinimalButtonBase = `
  width: 1.4em;
  height: 1.4em;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${h.getHoverColor("button")};
  }
`;
export const KeypadButtonMinimal = styled(KeypadButton)`
  ${MinimalButtonBase};
`;

export const SpinControlKeypadButtonMinimal = styled(SpinControlKeypadButton)`
  ${MinimalButtonBase};
`;

export const StyledAutoplayButtonMinimal = styled(AutoplayButton)`
  ${MinimalButtonBase};
  color: ${h.getColor(
    "autoplayButtonMinimalIcon",
    "autoplayButtonMinimal.icon"
  )};

  p {
    font-size: ${(props: IAutoplayButton) =>
      props.autoplayRoundsLeft > 999 ? "0.45em" : "0.55em"};
  }

  &:hover {
    color: ${h.getHoverColor(
      "autoplayButtonMinimalIcon",
      "autoplayButtonMinimal.icon"
    )};
  }

  &:active {
    color: ${h.getPressedColor(
      "autoplayButtonMinimalIcon",
      "autoplayButtonMinimal.icon"
    )};
  }

  &[disabled] {
    color: ${h.getDisabledColor(
      "autoplayButtonMinimalIcon",
      "autoplayButtonMinimal.icon"
    )};
  }

  &::before {
    font-size: 0.6em;
    color: ${h.getColor(
      "autoplayButtonMinimalText",
      "autoplayButtonMinimal.text"
    )};

    &:hover {
      color: ${h.getHoverColor(
        "autoplayButtonMinimalText",
        "autoplayButtonMinimal.text"
      )};
    }

    &:active {
      color: ${h.getPressedColor(
        "autoplayButtonMinimalText",
        "autoplayButtonMinimal.text"
      )};
    }

    &[disabled] {
      color: ${h.getDisabledColor(
        "autoplayButtonMinimalText",
        "autoplayButtonMinimal.text"
      )};
    }
  }
`;
